import { useMemo, useState } from 'react'
import { CalendarDays, Check, ChevronDown } from 'lucide-react'
import { Button } from '@quebon/ui/components/button'
import { Input } from '@quebon/ui/components/input'
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from '@quebon/ui/components/popover'
import type { BriefingSessionAdmin } from '@/api/lcMarketing'
import { buildBriefingSessionOptions, filterBriefingSessionOptions } from './briefingSessionFilterModel'

export function BriefingSessionFilter({ sessions, value, onChange }: {
  sessions: BriefingSessionAdmin[]
  value: string
  onChange: (value: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const options = useMemo(() => buildBriefingSessionOptions(sessions), [sessions])
  const visible = filterBriefingSessionOptions(options, query)
  const selected = options.find((option) => option.value === value)
  const select = (next: string) => {
    onChange(next)
    setQuery('')
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger render={<Button variant="outline" className="min-w-64 justify-between" aria-label="회차 선택" />}>
        <CalendarDays data-icon="inline-start" />
        <span>{selected ? `${selected.label} · ${selected.modeLabel}` : '전체 회차'}</span>
        <ChevronDown data-icon="inline-end" />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96">
        <PopoverTitle>사업설명회 회차 선택</PopoverTitle>
        <Input aria-label="회차 검색" placeholder="날짜 또는 방식 검색" value={query} onChange={(event) => setQuery(event.target.value)} />
        <div className="flex max-h-80 flex-col gap-1 overflow-y-auto">
          {!query && <Button variant={value ? 'ghost' : 'secondary'} className="justify-start" onClick={() => select('')}>전체 회차</Button>}
          {visible.map((option) => (
            <Button key={option.value} variant={value === option.value ? 'secondary' : 'ghost'} className="h-auto justify-between py-2" onClick={() => select(option.value)}>
              <span className="flex flex-col items-start gap-1">
                <span>{option.label} · {option.modeLabel}</span>
                <span className="text-xs text-muted-foreground">{option.attendanceLabel}</span>
              </span>
              {value === option.value && <Check data-icon="inline-end" />}
            </Button>
          ))}
          {visible.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">일치하는 회차가 없습니다.</p>}
        </div>
      </PopoverContent>
    </Popover>
  )
}
